<script lang="ts">
  import { Link } from "@inertiajs/svelte";
  import UserMenu from "../overlay/UserMenu.svelte";

  let {
    user,
    currentPath,
    showUserMenu,
    showMobileMenu,
    onSearchClick,
    onToggleUserMenu,
    onToggleMobileMenu,
    onLogout,
  }: {
    user: { id: string; name: string; email: string; avatarUrl?: string } | null;
    currentPath: string;
    showUserMenu: boolean;
    showMobileMenu: boolean;
    onSearchClick: () => void;
    onToggleUserMenu: (e?: MouseEvent) => void;
    onToggleMobileMenu: () => void;
    onLogout: () => void;
  } = $props();

  function isActive(path: string): boolean {
    return currentPath.startsWith(path);
  }

  function setColor(event: MouseEvent, color: string) {
    (event.currentTarget as HTMLElement).style.color = color;
  }

  function setColorAndBackground(event: MouseEvent, color: string, background: string) {
    (event.currentTarget as HTMLElement).style.color = color;
    (event.currentTarget as HTMLElement).style.background = background;
  }
</script>

<nav
  class="sticky top-0 z-40 safe-area-top"
  style="background: var(--glass-bg); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); border-bottom: 1px solid var(--border);"
>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-between h-16">
      <!-- Logo + Desktop links -->
      <div class="flex items-center">
        <Link href="/" class="text-2xl font-bold" style="color: var(--accent);">
          TripHelper
        </Link>

        {#if user}
          <div class="hidden md:flex ml-10 space-x-1">
            <Link
              href="/trips"
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              style="color: {isActive('/trips') ? 'var(--accent)' : 'var(--text-secondary)'}; background: {isActive('/trips') ? 'var(--accent-dim)' : 'transparent'};"
            >
              내 여행
            </Link>
            <Link
              href={`/users/${user.id}`}
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              style="color: var(--text-secondary);"
              onmouseenter={(e: MouseEvent) => setColor(e, 'var(--accent)')}
              onmouseleave={(e: MouseEvent) => setColor(e, 'var(--text-secondary)')}
            >
              내 프로필
            </Link>
          </div>
        {/if}
      </div>

      <!-- Right side actions -->
      <div class="flex items-center space-x-3">
        {#if user}
          <!-- Search -->
          <button
            onclick={onSearchClick}
            class="p-2 rounded-lg transition-colors duration-200 touch-target"
            style="color: var(--text-secondary);"
            title="사용자 검색"
            aria-label="사용자 검색"
            onmouseenter={(e: MouseEvent) => setColorAndBackground(e, 'var(--accent)', 'var(--accent-dim)')}
            onmouseleave={(e: MouseEvent) => setColorAndBackground(e, 'var(--text-secondary)', 'transparent')}
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          <!-- User Menu -->
          <div class="relative">
            <button
              onclick={(e) => { e.stopPropagation(); onToggleUserMenu(e); }}
              class="flex items-center space-x-2 focus:outline-none cursor-pointer"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                style="background: var(--accent-dim); color: var(--accent);"
              >
                {user.name.charAt(0)}
              </div>
              <span class="hidden md:block text-sm font-medium" style="color: var(--text-secondary);">
                {user.name}님
              </span>
              <svg class="w-4 h-4 hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <UserMenu {user} show={showUserMenu} onLogout={onLogout} />
          </div>
        {:else}
          <Link href="/login" class="text-sm font-medium transition-colors duration-200" style="color: var(--text-secondary);"
            onmouseenter={(e: MouseEvent) => setColor(e, 'var(--text-primary)')}
            onmouseleave={(e: MouseEvent) => setColor(e, 'var(--text-secondary)')}
          >
            로그인
          </Link>
          <Link href="/signup" class="btn btn-primary text-sm">
            시작하기
          </Link>
        {/if}
      </div>

      <!-- Mobile hamburger (desktop nav only) — hidden if we have pill nav -->
      {#if !user}
        <div class="flex items-center md:hidden">
          <button
            onclick={onToggleMobileMenu}
            class="p-2 rounded-lg transition-colors touch-target"
            style="color: var(--text-secondary);"
            aria-label="메뉴"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {#if showMobileMenu}
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              {:else}
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              {/if}
            </svg>
          </button>
        </div>
      {/if}
    </div>
  </div>

  <!-- Mobile menu (unauthenticated) -->
  {#if showMobileMenu && !user}
    <div class="md:hidden safe-area-bottom" style="border-top: 1px solid var(--border);">
      <div class="px-4 py-3 space-y-1">
        <Link href="/login" class="block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors" style="color: var(--text-secondary);">
          로그인
        </Link>
        <Link href="/signup" class="block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors" style="color: var(--accent);">
          회원가입
        </Link>
      </div>
    </div>
  {/if}
</nav>
